import { useEffect, useMemo, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useGetList, useGetOne } from 'react-admin';

import { projectOptionLabel, type SimpleRecord } from './receiptHelpers';

type Props = {
    value: string;
    onChange: (projectId: string) => void;
    onProjectSelect?: (project: SimpleRecord | null) => void;
    required?: boolean;
    label?: string;
    disabled?: boolean;
    /** Extra getList filter merged with the search text, e.g. { phase: 'active' }. */
    extraFilter?: Record<string, unknown>;
};

export const ProjectSearchAutocomplete = ({
    value,
    onChange,
    onProjectSelect,
    required = false,
    label = 'Project',
    disabled = false,
    extraFilter,
}: Props) => {
    const [inputValue, setInputValue] = useState('');
    const q = inputValue.trim();
    const selectedId = value ? Number(value) : null;

    const { data: searchResults = [], isFetching } = useGetList<SimpleRecord>(
        'projects',
        {
            pagination: { page: 1, perPage: 20 },
            sort: { field: 'project_no', order: 'DESC' },
            filter: { ...extraFilter, ...(q ? { q } : {}) },
        },
        { enabled: q.length > 0 }
    );

    const { data: selectedProject } = useGetOne<SimpleRecord>(
        'projects',
        { id: selectedId as number },
        { enabled: selectedId != null && !Number.isNaN(selectedId) }
    );

    useEffect(() => {
        if (!selectedProject) return;
        setInputValue(projectOptionLabel(selectedProject));
        onProjectSelect?.(selectedProject);
        // ponytail: omit onProjectSelect from deps — callers pass inline lambdas.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProject]);

    useEffect(() => {
        if (value) return;
        onProjectSelect?.(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const options = useMemo(() => {
        const byId = new Map<number, SimpleRecord>();
        if (selectedProject) byId.set(selectedProject.id, selectedProject);
        searchResults.forEach(project => byId.set(project.id, project));
        return [...byId.values()];
    }, [searchResults, selectedProject]);

    const selected =
        options.find(project => String(project.id) === value) ||
        selectedProject ||
        null;

    return (
        <Autocomplete
            fullWidth
            disabled={disabled}
            options={options}
            value={selected}
            inputValue={inputValue}
            onInputChange={(_, next, reason) => {
                // Async option loads fire reason=reset; applying it clears the
                // typed query (worse on iOS Safari) so results never stick.
                if (reason === 'reset') return;
                setInputValue(next);
            }}
            onChange={(_, project) => {
                onChange(project ? String(project.id) : '');
                onProjectSelect?.(project ?? null);
                setInputValue(project ? projectOptionLabel(project) : '');
            }}
            getOptionLabel={projectOptionLabel}
            isOptionEqualToValue={(option, optionValue) => option.id === optionValue.id}
            filterOptions={x => x}
            loading={isFetching}
            noOptionsText={q ? 'No projects found' : 'Type to search projects'}
            renderInput={params => (
                <TextField
                    {...params}
                    label={label}
                    required={required}
                    placeholder="Search by project no, name, or address"
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {isFetching ? (
                                    <CircularProgress color="inherit" size={16} />
                                ) : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
};
